import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Pagination } from "semantic-ui-react";
import { getCallRecordings } from "../../api/transcriptions";
import { useMeetingNotesTemplates } from "../../hooks/useMeetingNotesTemplates";
import { DataTable, ui } from "../ui";
import CallRecordingRow from "./CallRecordingRow";
import {
  RecordingsError,
  RecordingsLoading,
  RecordingsNotice,
  useRecordingsQuery,
} from "./CallRecordingsStatus";

const RECORDING_COLUMNS = [
  { key: "extension", label: "Extension" },
  { key: "number", label: "Number" },
  { key: "direction", label: "Direction" },
  { key: "start", label: "Start Time" },
  { key: "end", label: "End Time" },
  { key: "timezone", label: "Timezone" },
  { key: "duration", label: "Duration" },
  // Fits the AI split button, so the column is the same width on every page
  { key: "actions", label: "", style: { width: "150px" } },
];

const CallRecordings = ({
  openCall,
  searchParams,
  setIsSearching,
  onViewProcessed,
}) => {
  // A page belongs to the search it was picked in, so a new search is on
  // page 1 from its first render (no fetch for the old page number).
  const [pageState, setPageState] = useState({ page: 1, searchParams });
  const page = pageState.searchParams === searchParams ? pageState.page : 1;

  const queryClient = useQueryClient();
  // Once here, not per row
  const templates = useMeetingNotesTemplates();

  const queryKey = ["call-recordings", page, searchParams];
  const status = useRecordingsQuery(
    queryKey,
    () => {
      return getCallRecordings({ ...searchParams, page });
    },
    {
      keepPreviousData: true,
      // Each fetch can mean another iPECS login, which can end the shared session
      staleTime: 60000,
      refetchIntervalInBackground: true,
      refetchInterval: (data) => {
        let interval = false;
        const recordings = data?.data?.data;
        if (recordings) {
          recordings.forEach((i) => {
            if (
              ["SUMMARISING", "TRANSCRIBING"].includes(
                i?.transcription?.status.value,
              )
            ) {
              queryClient.invalidateQueries({
                queryKey: ["call-transcriptions"],
              });
              interval = 30 * 1000;
            }
          });
        }
        return interval;
      },
    },
  );

  const recordingsQuery = status.query;

  useEffect(() => {
    setIsSearching(recordingsQuery.isRefetching);
  }, [setIsSearching, recordingsQuery.isRefetching]);
  // Processed shares the search button, so don't leave it spinning
  useEffect(() => () => setIsSearching(false), [setIsSearching]);

  const handlePaginationChange = (e, { activePage }) => {
    setPageState({ page: activePage, searchParams });
  };

  if (status.mode === "loading") {
    return (
      <div>
        <RecordingsLoading
          slow={status.slow}
          ipecsIssue={status.ipecsIssue}
          attempt={status.attempt}
          maxAttempts={status.maxAttempts}
          columns={8}
        />
      </div>
    );
  }

  if (status.mode === "error") {
    return (
      <div>
        <RecordingsError
          error={recordingsQuery.error}
          retrying={status.retrying}
          retryIn={status.retryIn}
          onRetry={status.retry}
          onViewProcessed={onViewProcessed}
        />
      </div>
    );
  }

  const meta = status.rows?.data?.meta;
  // Rows from another search don't match its pages, but a failed page change
  // within the same search can still page back.
  const sameSearch =
    !status.fallbackKey ||
    JSON.stringify(status.fallbackKey[2]) === JSON.stringify(searchParams);
  const paginationEnabled = meta?.last_page > 1 && sameSearch;
  const recordings = status.rows?.data?.data || [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      {status.notice && (
        <RecordingsNotice
          kind={status.notice}
          ipecsIssue={status.ipecsIssue}
          updatedAt={status.updatedAt}
          attempt={status.attempt}
          maxAttempts={status.maxAttempts}
          retrying={status.retrying}
          retryIn={status.retryIn}
          onRetry={status.retry}
        />
      )}
      <DataTable columns={RECORDING_COLUMNS} empty="No call recordings found.">
        {recordings.map((i) => {
          return (
            <CallRecordingRow
              id={i.slug}
              recordingRef={i.recording_ref}
              key={`recording-row-${i.slug}`}
              number={i.tel_number}
              direction={i.direction}
              start={i.start}
              end={i.end}
              extension={i.ext}
              duration={i.duration}
              timezone={i.effective_timezone}
              transcription={i.transcription}
              openCall={openCall}
              templates={templates}
              searchParams={searchParams}
              actionsDisabledReason={status.actionsDisabledReason}
            />
          );
        })}
      </DataTable>

      {meta && paginationEnabled ? (
        <div style={{ width: "100%", textAlign: "center" }}>
          <Pagination
            inverted
            activePage={page}
            totalPages={meta.last_page}
            onPageChange={handlePaginationChange}
          />
        </div>
      ) : (
        <div style={{ ...ui.muted, fontSize: "13px" }}>
          <span>
            Showing {recordings.length} result
            {recordings.length !== 1 ? "s" : ""}
          </span>
        </div>
      )}
    </div>
  );
};

export default CallRecordings;
